if (!customElements.get("f-cart-goal")) {
  class FCartGoal extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        cartDrawer: ".f-cart-drawer__inner",
        leftToSpend: "[data-left-to-spend]",
        minimumLeftToSpend: "[data-minimum-left-to-spend]",
        cartGoalTitle: ".f-cartgoal__title",
      };

      this.confettiShow = false;
      this.goalDone = false;
      this.lastCartTotal = null;
      this.cartUpdateUnsubscriber = null;
      this.refreshTimers = [];
      this.refreshRequestId = 0;
      this.handleExternalCartUpdate = this.scheduleCartRefresh.bind(this);
      this.goal =
        Number(this.dataset.cartGoal) *
          Number(window.Shopify.currency.rate || 1) || 0;
      this.minimumOrderGoal =
        Number(this.dataset.minimumOrderGoal) *
          Number(window.Shopify.currency.rate || 1) || 0;
      this.minimumOrderEnabled =
        this.dataset.minimumOrderEnabled === "true" &&
        this.minimumOrderGoal > 0 &&
        this.minimumOrderGoal < this.goal;
      this.money_format = window.FoxThemeSettings.money_format;
      this.enableConfetti = this.dataset.enableConfetti === "true";

      if (this.minimumOrderEnabled) {
        const milestonePosition = (this.minimumOrderGoal * 100) / this.goal;
        this.style.setProperty("--minimum-order-position", `${milestonePosition}%`);
      }
    }

    connectedCallback() {
      this.updateCartGoal(Number(this.dataset.cartTotal));
      this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (state) => {
          const cartTotal = Number(state?.items_subtotal_price);
          if (Number.isFinite(cartTotal)) {
            this.updateCartGoal(cartTotal);
          }

          this.scheduleCartRefresh();
        }
      );
      document.addEventListener("cart:updated", this.handleExternalCartUpdate);
      document.addEventListener("cart:refresh", this.handleExternalCartUpdate);

      // Reconcile with the authoritative cart state when this component is
      // inserted or replaced by a third-party cart integration.
      this.scheduleCartRefresh();
      this.confettiShow = this.goalDone;
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }

      document.removeEventListener("cart:updated", this.handleExternalCartUpdate);
      document.removeEventListener("cart:refresh", this.handleExternalCartUpdate);
      this.clearScheduledRefreshes();
      this.refreshRequestId += 1;
    }

    clearScheduledRefreshes() {
      this.refreshTimers.forEach((timer) => window.clearTimeout(timer));
      this.refreshTimers = [];
    }

    scheduleCartRefresh() {
      this.clearScheduledRefreshes();

      // EG and similar apps can mutate the cart in a second asynchronous
      // request. A bounded pair of checks catches both the theme update and
      // the final app-managed state without polling continuously.
      [250, 1000].forEach((delay) => {
        const timer = window.setTimeout(() => this.refreshCartGoal(), delay);
        this.refreshTimers.push(timer);
      });
    }

    async refreshCartGoal() {
      const requestId = ++this.refreshRequestId;

      try {
        const newCart = await window.FoxThemeCartHelpers.getState();
        if (requestId !== this.refreshRequestId || !this.isConnected || !newCart) {
          return;
        }

        this.updateCartGoal(newCart.items_subtotal_price);
      } catch (error) {
        console.error("Unable to refresh cart goal:", error);
      }
    }

    updateCartGoal(state) {
      state = Number(state);
      if (!Number.isFinite(state) || this.lastCartTotal === state) return;
      this.lastCartTotal = state;

      if (state > 0) {
        this.classList.remove("hidden");
      } else {
        this.classList.add("hidden");
      }

      this.cartTotal = state / 100;
      this.goalLeft = this.goal - this.cartTotal;
      this.goalDone = this.goalLeft <= 0;

      const minimumOrderReached =
        this.minimumOrderEnabled && this.cartTotal >= this.minimumOrderGoal;
      this.classList.toggle(
        "f-cartgoal--minimum-pending",
        this.minimumOrderEnabled && !minimumOrderReached
      );
      this.classList.toggle(
        "f-cartgoal--minimum-reached",
        minimumOrderReached
      );

      this.percent = (this.cartTotal * 100) / this.goal;

      if (this.percent >= 100) this.percent = 100;

      if (this.cartTotal >= this.goal) {
        this.style.setProperty("--progress", `${this.percent}%`);
        this.classList.add("f-cartgoal--done", "shakeY");
        this.dataset.total = this.cartTotal;
      } else {
        let spend = (this.goal - this.cartTotal) * 100;
        this.querySelectorAll(this.selectors.leftToSpend).forEach((leftToSpend) => {
          leftToSpend.innerHTML = formatMoney(spend, this.money_format);
        });

        if (this.minimumOrderEnabled && !minimumOrderReached) {
          const minimumLeftToSpend = this.querySelector(
            this.selectors.minimumLeftToSpend
          );
          const minimumSpend = (this.minimumOrderGoal - this.cartTotal) * 100;
          if (minimumLeftToSpend) {
            minimumLeftToSpend.innerHTML = formatMoney(
              minimumSpend,
              this.money_format
            );
          }
        }

        this.classList.remove("f-cartgoal--done", "shakeY");
        this.style.setProperty("--progress", `${this.percent}%`);
        this.dataset.total = this.cartTotal;
      }

      if (this.goalLeft <= 0 && this.enableConfetti) {
        this.showConfetti();
      } else {
        this.confettiShow = false;
      }
    }

    showConfetti() {
      const cartDrawer = this.closest(this.selectors.cartDrawer);
      const cartTemplate = this.closest(".cart-template");

      if (!this.confettiShow && (cartDrawer || cartTemplate)) {
        setTimeout(() => {
          if (cartDrawer) {
            new window.FoxTheme.Confetti(cartDrawer);
          }
          if (cartTemplate) {
            new window.FoxTheme.Confetti(document.body);
          }
          this.confettiShow = true;
        }, 800);
      }
    }
  }

  customElements.define("f-cart-goal", FCartGoal);
}
